import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, LinearProgress, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { AppDialog } from "components/common/dialogs";
import { AppButton } from "components/common/buttons";
import { AppTextField } from "components/common/inputs";
import PersonnelOrganizationFilter from "components/common/filters/PersonnelOrganizationFilter";
import DataTable from "components/common/tables/DataTable";
import usePersonnelOrganization from "../../hooks/usePersonnelOrganization";
import { fetchPersonnel } from "../../services/personnel/personnel.service";

const columns = [
  { accessorKey: "fullName", header: "Họ và tên", size: 180 },
  { accessorKey: "title", header: "Chức vụ", size: 150 },
  { accessorKey: "email", header: "Email", size: 200 },
  { accessorKey: "phoneNumber", header: "Số điện thoại", size: 130 },
  {
    accessorKey: "isActive", header: "Trạng thái", size: 80,
    Cell: ({ cell }) => {
      const active = cell.getValue();
      const label = active ? "Đã kích hoạt" : "Chưa kích hoạt";
      return <Tooltip title={label}>
        <Box component="span" role="img" aria-label={label} sx={{
          display: "inline-block", width: 12, height: 12, borderRadius: "50%",
          backgroundColor: active ? "#4caf50" : "#d32f2f",
        }} />
      </Tooltip>;
    },
  },
];

// Mounted only while the picker is open, so the account form does not preload personnel.
export default function PersonnelPickerDialog({ onClose, onSelect, selectedPersonnelId }) {
  const organization = usePersonnelOrganization(true);
  const departments = useMemo(() => organization.departments.filter((department) =>
    department.enterpriseId && department.departmentId), [organization.departments]);
  const [filters, setFilters] = useState({ enterpriseId: null, departmentId: null });
  const enterpriseId = departments.find((department) => department.enterpriseId === filters.enterpriseId)?.enterpriseId
    ?? departments[0]?.enterpriseId ?? null;
  const branchDepartments = departments.filter((department) => department.enterpriseId === enterpriseId);
  const departmentId = branchDepartments.find((department) => department.departmentId === filters.departmentId)?.departmentId
    ?? branchDepartments[0]?.departmentId ?? null;
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState({ key: "", items: [], total: 0, loading: false, error: "" });
  const queryKey = JSON.stringify([enterpriseId, departmentId, pagination.pageIndex, pagination.pageSize, searchTerm]);
  const searchPending = search.trim() !== searchTerm;
  const resultMatches = result.key === queryKey;
  const loading = organization.loading || searchPending || (departmentId != null && (!resultMatches || result.loading));
  const error = resultMatches ? result.error : "";
  const items = resultMatches && !searchPending && !organization.loading && !organization.error ? result.items : [];

  useEffect(() => {
    if (search.trim() === searchTerm) return;
    const timer = setTimeout(() => {
      setSearchTerm(search.trim());
      setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, searchTerm]);

  useEffect(() => {
    if (organization.loading || organization.error || enterpriseId == null || departmentId == null || searchPending) return;
    let current = true;
    setResult({ key: queryKey, items: [], total: 0, loading: true, error: "" });
    fetchPersonnel(pagination.pageSize, pagination.pageIndex + 1, searchTerm || null, "FullName", "ASC", null, {
      enterpriseId, departmentId,
    }).then((response) => {
      if (!current) return;
      setResult({
        key: queryKey, loading: false, error: "", total: response.totalItems,
        items: (response.items || []).map((item) => ({
          ...item,
          personnelId: item.personnelId ?? item.PersonnelId,
          personnelCode: item.personnelCode ?? item.PersonnelCode ?? "",
          fullName: item.fullName ?? item.FullName ?? item.personnelName ?? item.PersonnelName ?? "",
          title: item.title ?? item.Title ?? "",
          email: item.email ?? item.Email ?? "",
          phoneNumber: item.phoneNumber ?? item.PhoneNumber ?? "",
          isActive: item.isActive ?? item.IsActive ?? false,
        })),
      });
    }).catch(() => {
      if (current) setResult({
        key: queryKey, items: [], total: 0, loading: false,
        error: "Không thể tải danh sách nhân sự. Vui lòng thử lại.",
      });
    });
    return () => { current = false; };
  }, [enterpriseId, departmentId, pagination.pageIndex, pagination.pageSize, searchTerm, searchPending,
    queryKey, reload, organization.loading, organization.error]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };

  return (
    <AppDialog
      open
      title="Chọn nhân sự"
      onClose={onClose}
      size="lg"
      actions={
        <AppButton variant="outlined" color="inherit" onClick={onClose}>
          Đóng
        </AppButton>
      }
    >
      <PersonnelOrganizationFilter {...organization} departments={departments} includeAll={false}
        value={{ enterpriseId, departmentId }} onChange={handleFiltersChange} />
      {!organization.loading && !organization.error && !departments.length && (
        <Alert severity="info">Chưa có phòng ban thuộc chi nhánh để chọn nhân sự.</Alert>
      )}
      <AppTextField label="Tìm nhân sự" placeholder="Nhập tên hoặc email nhân sự" value={search} onChange={setSearch}
        maxLength={100}
        startIcon={<SearchIcon fontSize="small" />} disabled={!departmentId || organization.loading || !!organization.error}
        sx={{ mb: 2 }} />
      {error && <Alert severity="error" sx={{ mb: 2 }}
        action={<AppButton onClick={() => setReload((count) => count + 1)}>Thử lại</AppButton>}>{error}</Alert>}
      {loading && <LinearProgress aria-label="Đang tải nhân sự" sx={{ mb: 1 }} />}
      <Box>
        <DataTable
          columns={columns}
          data={items}
          getRowId={(row) => String(row.personnelId)}
          manualPagination
          manualFiltering
          rowCount={resultMatches && !searchPending ? result.total : 0}
          state={{ pagination, isLoading: loading }}
          onPaginationChange={setPagination}
          autoResetPageIndex={false}
          rowsPerPageOptions={[10, 20, 50]}
          enableGlobalFilter={false}
          enableTopToolbar={false}
          enableRowActions
          maxTableHeight="calc(100dvh - 360px)"
          renderEmptyRowsFallback={() =>
            <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              {loading ? "Đang tải danh sách nhân sự…" : error || organization.error ? "Chưa tải được danh sách nhân sự." : "Không có nhân sự phù hợp."}
            </Box>
          }
          renderRowActions={({ row }) =>
            <AppButton size="small" variant="outlined"
              disabled={loading || !!error || !!organization.error}
              aria-label={`Chọn ${row.original.fullName}`} onClick={() => onSelect(row.original)}>
              {row.original.personnelId === selectedPersonnelId ? "Đã chọn" : "Chọn"}
            </AppButton>
          }
        />
      </Box>
    </AppDialog>
  );
}
